import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Crown, Layers, Flame, Radar } from "lucide-react";
import { BrandMark } from "@/components/Brand";

const GOLD = { bg: "#0A0907", panel: "#12100B", line: "rgba(201,162,74,0.22)", text: "#F5EEDC", mute: "rgba(245,238,220,0.55)", a: "#C9A24A", b: "#E9C46A", c: "#E8A0A0", btnText: "#0A0907" };
export const THEMES = {
  gold: GOLD,
  navy: { bg: "#050B1A", panel: "#0B1730", line: "rgba(96,165,250,0.22)", text: "#EAF1FF", mute: "rgba(234,241,255,0.58)", a: "#3B82F6", b: "#34D399", c: "#FB7185", btnText: "#FFFFFF" },
  ivory: { light: true, bg: "#F6F4EE", panel: "#FFFFFF", line: "rgba(15,23,42,0.12)", text: "#0F172A", mute: "rgba(15,23,42,0.6)", a: "#047857", b: "#059669", c: "#E11D48", btnText: "#FFFFFF" },
  amber: { bg: "#000000", panel: "#0C0C0C", line: "rgba(255,159,28,0.25)", text: "#FAFAFA", mute: "rgba(250,250,250,0.55)", a: "#FF9F1C", b: "#22C55E", c: "#EF4444", btnText: "#000000" },
};
let t = GOLD;
const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// Order-flow streams and drifting candles that move away from the cursor
const FlowCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current, ctx = cv.getContext("2d"), r = devicePixelRatio;
    let w, h, raf, mouse = { x: -999, y: -999 };
    const resize = () => { w = cv.width = cv.offsetWidth * r; h = cv.height = cv.offsetHeight * r; };
    resize();
    const P = Array.from({ length: 140 }, () => ({ x: Math.random(), y: Math.random(), v: 0.0006 + Math.random() * 0.0018, l: 20 + Math.random() * 90, c: Math.random() < 0.7 ? t.a : t.b }));
    const C = Array.from({ length: 34 }, (_, i) => ({ x: i / 34, y: 0.55 + Math.sin(i * 0.6) * 0.12 + Math.random() * 0.05, s: 0.6 + Math.random() * 0.8, up: Math.random() > 0.4 }));
    const move = (e) => { const b = cv.getBoundingClientRect(); mouse = { x: (e.clientX - b.left) * r, y: (e.clientY - b.top) * r }; };
    const push = (x, y, rad, k) => { const dx = x - mouse.x, dy = y - mouse.y, d = Math.hypot(dx, dy) || 1; return d < rad * r ? y + (dy / d) * (rad * r - d) * k : y; };
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      C.forEach((c) => {
        c.x -= 0.00025; if (c.x < -0.05) c.x = 1.05;
        const x = c.x * w, y = push(x, c.y * h, 220, 0.25), bh = 50 * c.s * r;
        ctx.globalAlpha = 0.16; ctx.fillStyle = ctx.strokeStyle = c.up ? t.b : t.c;
        ctx.fillRect(x - 5 * r, y - bh / 2, 10 * r, bh);
        ctx.beginPath(); ctx.moveTo(x, y - bh); ctx.lineTo(x, y + bh); ctx.stroke();
      });
      P.forEach((p) => {
        p.x += p.v; if (p.x > 1.1) { p.x = -0.1; p.y = Math.random(); }
        const x = p.x * w, y = push(x, p.y * h, 180, 0.4);
        const g = ctx.createLinearGradient(x - p.l * r, y, x, y);
        g.addColorStop(0, "transparent"); g.addColorStop(1, p.c);
        ctx.globalAlpha = 0.55; ctx.strokeStyle = g; ctx.lineWidth = 1.2 * r;
        ctx.beginPath(); ctx.moveTo(x - p.l * r, y); ctx.lineTo(x, y); ctx.stroke();
      });
      ctx.globalAlpha = 1; raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize); window.addEventListener("mousemove", move);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); window.removeEventListener("mousemove", move); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 h-full w-full" data-testid="flow-canvas" />;
};

const FEED = [
  ["RELIANCE", "TFR Gold · Swing", "Node-H cleared", 1], ["HDFCBANK", "Value Nodes", "Above Node-H", 1], ["TATASTEEL", "Power Scan", "Range Matrix breakout", 1],
  ["INFY", "Value Nodes", "Below Node-L", 0], ["ITC", "Blue Elephant", "Institutional footprint ★★★", 1], ["LT", "Opening Thrust", "Strong opening conviction", 1],
  ["SBIN", "TFR Gold · Intraday", "TFR levels aligned", 1], ["MARUTI", "Power Scan", "Range Matrix breakdown", 0],
];
const TABS = [[Crown, "TFR Gold"], [Layers, "Value Nodes"], [Flame, "Power Scan"], [Radar, "Blue Elephant"]];
const Spark = ({ up }) => (
  <svg viewBox="0 0 60 20" className="h-5 w-16"><polyline fill="none" stroke={up ? t.b : t.c} strokeWidth="1.6" points={up ? "0,16 10,14 20,15 30,10 40,11 50,5 60,3" : "0,4 10,6 20,5 30,10 40,9 50,15 60,17"} /></svg>
);

// Illustrated scanner: signal tags only, no prices, labelled as an illustration
const ScannerMock = () => {
  const [n, setN] = useState(0);
  useEffect(() => { const id = setInterval(() => setN((x) => x + 1), 2400); return () => clearInterval(id); }, []);
  const rows = Array.from({ length: 5 }, (_, i) => FEED[(n + 5 - i) % FEED.length]);
  return (
    <div className="text-left overflow-hidden rounded-2xl border backdrop-blur-xl shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)]" style={{ borderColor: t.line, background: `${t.panel}E6` }} data-testid="scanner-mock">
      <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: t.line }}>
        {["#FF5F57", "#FEBC2E", "#28C840"].map((c) => <span key={c} className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />)}
        <span className="ml-3 text-[11px] font-mono" style={{ color: t.mute }}>TFR INSTITUTIONAL SCANNER</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono" style={{ color: t.b }}><span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: t.b }} />LIVE</span>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b px-3 py-2" style={{ borderColor: t.line }}>
        {TABS.map(([I, l], i) => (
          <span key={l} className="flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-semibold" style={i === 0 ? { background: t.a, color: t.btnText } : { color: t.mute }}><I className="h-3.5 w-3.5" />{l}</span>
        ))}
      </div>
      <div className="h-[218px] overflow-hidden px-3 py-2">
        <AnimatePresence initial={false} mode="popLayout">
          {rows.map((r) => (
            <motion.div key={`${r[0]}-${r[1]}`} layout initial={{ opacity: 0, y: -18, backgroundColor: `${t.a}33` }} animate={{ opacity: 1, y: 0, backgroundColor: "rgba(0,0,0,0)" }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
              className="grid grid-cols-12 items-center gap-2 rounded-lg px-2 py-2.5 text-xs" style={{ color: t.text }}>
              <span className="col-span-4 sm:col-span-3 font-mono font-bold truncate">{r[0]}</span>
              <span className="hidden sm:block col-span-3 truncate" style={{ color: t.mute }}>{r[1]}</span>
              <span className="col-span-6 sm:col-span-4 truncate font-medium" style={{ color: r[3] ? t.b : t.c }}>{r[3] ? "▲" : "▼"} {r[2]}</span>
              <span className="col-span-2 justify-self-end"><Spark up={r[3]} /></span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <div className="border-t px-4 py-2 text-[9px] font-mono" style={{ borderColor: t.line, color: t.mute }}>ILLUSTRATION · NOT LIVE MARKET DATA</div>
    </div>
  );
};

const Tilt = ({ children }) => {
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [10, -10]), { stiffness: 80, damping: 15 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-12, 12]), { stiffness: 80, damping: 15 });
  useEffect(() => {
    const f = (e) => { mx.set((e.clientX / window.innerWidth) * 2 - 1); my.set((e.clientY / window.innerHeight) * 2 - 1); };
    window.addEventListener("mousemove", f); return () => window.removeEventListener("mousemove", f);
  }, [mx, my]);
  return <div style={{ perspective: 1400 }}><motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>{children}</motion.div></div>;
};

const Cta = ({ testid, className = "px-8 py-4" }) => (
  <button onClick={() => go("pricing")} data-testid={testid} className={`group inline-flex items-center rounded-full text-sm font-semibold transition-transform hover:-translate-y-0.5 ${className}`} style={{ background: t.a, color: t.btnText, boxShadow: `0 0 60px -10px ${t.a}` }}>
    Get access <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
  </button>
);

const Nav = ({ onSignIn }) => (
  <header className="relative z-20 max-w-7xl mx-auto px-6 h-20 flex items-center gap-8">
    <BrandMark className="h-10 w-10" />
    <nav className="hidden md:flex gap-7 text-sm" style={{ color: t.mute }}>
      {[["features", "Engines"], ["pricing", "Pricing"], ["faq", "FAQ"]].map(([id, l]) => (
        <button key={id} onClick={() => go(id)} className="transition-colors hover:text-[#F5EEDC]" data-testid={`landing-nav-${id}`}>{l}</button>
      ))}
    </nav>
    <div className="flex-1" />
    <button onClick={onSignIn} className="text-sm transition-colors hover:text-[#C9A24A]" style={{ color: t.text }} data-testid="landing-login">Sign in</button>
    <button onClick={() => go("pricing")} className="rounded-full px-5 py-2 text-sm font-semibold transition-[filter] hover:brightness-110" style={{ background: t.a, color: t.btnText }} data-testid="landing-cta">Get access</button>
  </header>
);

export const LandingHero = ({ onSignIn, theme = GOLD }) => {
  t = theme;
  return (
  <section className="relative min-h-screen overflow-hidden" style={{ background: t.bg, color: t.text }} data-testid="landing-hero">
    {!t.light && <motion.img src="/landing/hero.webp" alt="" initial={{ scale: 1.3, x: "4%" }} animate={{ scale: 1.08, x: "0%" }} transition={{ duration: 18, ease: "easeOut" }} className="absolute inset-0 h-full w-full object-cover opacity-35 mix-blend-screen" />}
    <FlowCanvas key={t.a} />
    {!t.light && <div className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:repeating-linear-gradient(0deg,#fff_0px,#fff_1px,transparent_1px,transparent_4px)]" />}
    <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${t.bg} 15%, ${t.bg}CC 45%, transparent 100%)` }} />
    <Nav onSignIn={onSignIn} />
    <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <span className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] font-mono tracking-[0.18em]" style={{ borderColor: t.line, color: t.a }}>
          <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: t.a }} />MADE BY DESK INSTITUTIONAL FLOW TRADERS
        </span>
        <motion.h1 initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9 }} className="tfr-display mt-7 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
          The scanner<br />institutional desks <span style={{ color: t.a }}>keep private.</span>
        </motion.h1>
        <p className="mt-6 max-w-md text-lg" style={{ color: t.mute }}>Five proprietary TFR engines read where institutions are positioned — and flag the moment price breaks away.</p>
        <div className="mt-9 flex flex-wrap items-center gap-5"><Cta testid="hero-cta" className="px-8 py-4 whitespace-nowrap" /><span className="text-sm font-mono" style={{ color: t.mute }}>Founding seats · ₹7,999 / month</span></div>
        <div className="mt-12 flex flex-wrap gap-10">
          {[["5", "TFR engines"], ["F&O + Nifty 500", "coverage"], ["Live", "through the session"]].map(([a, b]) => <div key={b}><div className="tfr-display text-2xl font-bold">{a}</div><div className="text-xs" style={{ color: t.mute }}>{b}</div></div>)}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.1, delay: 0.3 }} className="lg:col-span-6"><Tilt><ScannerMock /></Tilt></motion.div>
    </div>
  </section>
  );
};
